import { useState } from "react";
import type { ComponentType, CSSProperties } from "react";
import Icon from "../Icon/Icon.jsx";
import {
  ResponsiveIFrame as ResponsiveIFrameUntyped,
  responsiveIFrameContainer,
} from "./ResponsiveIFrame.jsx";
import styles from "./VideoGate.css";

// `ResponsiveIFrame.jsx` is untyped JS, so give its exports the precise shapes
// we rely on.
const ResponsiveIFrame = ResponsiveIFrameUntyped as ComponentType<{
  ratio?: number;
  src: string;
  allow?: string;
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  title?: string;
}>;

export type VideoService =
  | "youtube"
  | "vimeo"
  | "arte"
  | "ardmediathek"
  | "zdf";

const providerLabels: Record<VideoService, string> = {
  youtube: "YouTube",
  vimeo: "Vimeo",
  arte: "arte",
  ardmediathek: "ARD Mediathek",
  zdf: "ZDF",
};

type VideoGateProps = {
  service: VideoService;
  id: string;
  /** The provider embed URL to load into the iframe on click. */
  embedSrc: string;
  /** `allow` attribute for the iframe (autoplay/fullscreen/…). */
  allow?: string;
  ratio: number;
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  /** arte only — language segment for the poster lookup. */
  lang?: string;
  /** Provider-specific autoplay query param, appended to `embedSrc` on load. */
  autoplayParam?: string;
  title?: string;
};

/**
 * Click-to-play gate for external video embeds. Until the user clicks, only a
 * poster image (served from our own `/video-poster/img` endpoint) is rendered —
 * the third-party iframe (and therefore any request to Google/Vimeo/ARD/ZDF/arte)
 * is not mounted, satisfying the GDPR "no third-party request before consent"
 * requirement. Consent is per-click and never persisted.
 */
export const VideoGate = ({
  service,
  id,
  embedSrc,
  allow,
  ratio,
  referrerPolicy,
  lang,
  autoplayParam,
  title,
}: VideoGateProps) => {
  const [loaded, setLoaded] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);

  if (loaded) {
    // Single click loads *and* plays — append the provider autoplay param.
    const src = autoplayParam
      ? `${embedSrc}${embedSrc.includes("?") ? "&" : "?"}${autoplayParam}`
      : embedSrc;

    return (
      <ResponsiveIFrame
        ratio={ratio}
        src={src}
        allow={allow}
        referrerPolicy={referrerPolicy}
        title={title}
      />
    );
  }

  const label = providerLabels[service];

  // Poster is served from our own (auth-protected) origin, so the third-party
  // provider is never contacted from the browser. `crossOrigin="use-credentials"`
  // forwards the session cookie (the endpoint is behind `withAuth`), and
  // `loading="lazy"` defers the request until the embed nears the viewport.
  const posterUrl = `${import.meta.env.VITE_API_HOST}/video-poster/img?service=${service}&id=${encodeURIComponent(
    id,
  )}${lang ? `&lang=${lang}` : ""}`;

  return (
    <div style={responsiveIFrameContainer(ratio) as CSSProperties}>
      <button
        type="button"
        className={styles.gate}
        onClick={() => setLoaded(true)}
        aria-label={`${label}-Video laden und abspielen`}
      >
        {posterFailed ? (
          <span className={styles.fallback}>
            <Icon name="video_library" size="48px" className={undefined} />
            <span className={styles.fallbackLabel}>{label}</span>
          </span>
        ) : (
          <img
            className={styles.poster}
            src={posterUrl}
            crossOrigin="use-credentials"
            loading="lazy"
            decoding="async"
            alt=""
            onError={() => setPosterFailed(true)}
          />
        )}
        <span className={styles.playButton}>
          <svg
            className={styles.playIcon}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
        <span className={styles.providerLabel}>{label}</span>
      </button>
    </div>
  );
};

export default VideoGate;
